import useDebounce from '@/hooks/useDebounce'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryClient } from '@tanstack/react-query'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useCallback, useEffect, useState } from 'react'
import { SEARCH_DEBOUNCE_DELAY, SEARCH_MIN_QUERY_LENGTH } from '../constants'
import {
    getRecentSearches,
    globalSearch,
    saveSearchQuery,
    getPopularSearches,
} from '../services'
import { SearchParams, SearchResponse, SearchResultType } from '../types'

interface UseGlobalSearchOptions {
    autoSearch?: boolean
    debounceDelay?: number
    minQueryLength?: number
}

export const useGlobalSearch = (options: UseGlobalSearchOptions = {}) => {
    const {
        autoSearch = true,
        debounceDelay = SEARCH_DEBOUNCE_DELAY,
        minQueryLength = SEARCH_MIN_QUERY_LENGTH,
    } = options

    const queryClient = useQueryClient()
    const [query, setQuery] = useState('')
    const [filters, setFilters] = useState<Partial<SearchParams>>({})
    const [selectedScopes, setSelectedScopes] = useState<SearchResultType[]>([
        'customer',
        'service',
        'contract',
    ])
    const [recentSearches, setRecentSearches] = useState<string[]>([])

    const debouncedQuery = useDebounce(query, debounceDelay)

    // Query for search results
    const {
        data: searchResponse,
        isLoading,
        error,
        refetch,
    } = useQueryWithHandler<SearchResponse>(
        {
            queryKey: [
                QUERY_KEYS.SEARCH_GLOBAL,
                debouncedQuery,
                filters,
                selectedScopes,
            ],
            queryFn: () =>
                globalSearch({
                    query: debouncedQuery,
                    scopes:
                        selectedScopes.length > 0 ? selectedScopes : undefined,
                    ...filters,
                }),
            enabled: autoSearch && debouncedQuery.length >= minQueryLength,
            staleTime: 1000 * 60 * 5, // 5 minutes
        },
        { showLoading: false, showError: false }
    )

    // Query for recent searches
    const { data: recentSearchesData, refetch: refetchRecentSearches } =
        useQueryWithHandler(
            {
                queryKey: [QUERY_KEYS.SEARCH_RECENT_SEARCHES],
                queryFn: getRecentSearches,
                staleTime: 1000 * 60 * 10, // 10 minutes
            },
            { showLoading: false, showError: false }
        )

    // Query for popular searches
    const { data: popularSearchesData, refetch: refetchPopularSearches } =
        useQueryWithHandler(
            {
                queryKey: [QUERY_KEYS.SEARCH_POPULAR_SEARCHES],
                queryFn: getPopularSearches,
                staleTime: 1000 * 60 * 30, // 30 minutes
            },
            { showLoading: false, showError: false }
        )

    useEffect(() => {
        if (recentSearchesData) {
            setRecentSearches(recentSearchesData)
        }
    }, [recentSearchesData])

    const saveRecentSearch = useCallback(
        async (searchQuery: string) => {
            console.log(
                '🔍 saveRecentSearch called with:',
                searchQuery,
                'minLength:',
                minQueryLength
            )

            if (searchQuery.length < minQueryLength) {
                console.log('❌ Query too short, skipping save')
                return
            }

            console.log('💾 Attempting to save search:', {
                searchQuery,
                selectedScopes,
            })

            try {
                await saveSearchQuery(searchQuery, selectedScopes, [])
                console.log('✅ Search saved successfully')

                // Invalidate and refetch both recent and popular searches cache
                queryClient.invalidateQueries({
                    queryKey: [QUERY_KEYS.SEARCH_RECENT_SEARCHES],
                })
                queryClient.invalidateQueries({
                    queryKey: [QUERY_KEYS.SEARCH_POPULAR_SEARCHES],
                })
                await refetchRecentSearches()
                await refetchPopularSearches()
                console.log('✅ Cache invalidated and refetched')
            } catch (error) {
                console.error('❌ Error saving recent search:', error)
            }
        },
        [
            minQueryLength,
            selectedScopes,
            queryClient,
            refetchRecentSearches,
            refetchPopularSearches,
        ]
    )

    // Debug functions - expose to window for manual testing
    useEffect(() => {
        if (typeof window !== 'undefined') {
            // @ts-ignore
            window.searchDebug = {
                saveTest: (query: string) => saveRecentSearch(query),
                getRecent: () => getRecentSearches(),
                getPopular: () => getPopularSearches(),
                currentState: {
                    query,
                    selectedScopes,
                    recentSearches,
                    popularSearches: popularSearchesData || [],
                },
            }
            console.log(
                '🔧 Search debug functions available at window.searchDebug'
            )
        }
    }, [
        saveRecentSearch,
        query,
        selectedScopes,
        recentSearches,
        popularSearchesData,
    ])

    const handleSearch = useCallback(
        async (searchQuery: string, searchFilters?: Partial<SearchParams>) => {
            console.log('🔍 handleSearch called with:', {
                searchQuery,
                searchFilters,
                minQueryLength,
            })

            if (searchQuery.length < minQueryLength) {
                console.log('❌ Query too short for search')
                return
            }

            try {
                const response = await globalSearch({
                    query: searchQuery,
                    scopes:
                        selectedScopes.length > 0 ? selectedScopes : undefined,
                    ...searchFilters,
                })

                console.log('✅ Search completed, now saving to recent...')
                // Save to recent searches only for manual searches
                await saveRecentSearch(searchQuery)

                return response
            } catch (error) {
                console.error('❌ Search error:', error)
                throw error
            }
        },
        [minQueryLength, selectedScopes, saveRecentSearch]
    )

    const handleRecentSearchClick = useCallback(
        async (searchQuery: string) => {
            setQuery(searchQuery)

            // Trigger search immediately but don't save to recent (it's already there)
            try {
                const response = await globalSearch({
                    query: searchQuery,
                    scopes:
                        selectedScopes.length > 0 ? selectedScopes : undefined,
                    ...filters,
                })
                return response
            } catch (error) {
                console.error('Search error:', error)
                throw error
            }
        },
        [selectedScopes, filters]
    )

    const clearQuery = useCallback(() => {
        setQuery('')
    }, [])

    const updateFilters = useCallback((newFilters: Partial<SearchParams>) => {
        setFilters((prev) => ({ ...prev, ...newFilters }))
    }, [])

    const clearFilters = useCallback(() => {
        setFilters({})
        setSelectedScopes(['customer', 'service', 'contract'])
    }, [])

    const updateScopes = useCallback((newScopes: SearchResultType[]) => {
        setSelectedScopes(newScopes)
    }, [])

    // Auto save recent search when auto search returns results
    useEffect(() => {
        if (
            autoSearch &&
            searchResponse?.results &&
            searchResponse.results.length > 0 &&
            debouncedQuery.length >= minQueryLength
        ) {
            console.log(
                '🤖 Auto search returned results, saving to recent:',
                debouncedQuery
            )
            saveRecentSearch(debouncedQuery)
        }
    }, [
        autoSearch,
        searchResponse,
        debouncedQuery,
        minQueryLength,
        saveRecentSearch,
    ])

    return {
        // State
        query,
        filters,
        selectedScopes,
        recentSearches,
        popularSearches: popularSearchesData || [],

        // Search results
        results: searchResponse?.results || [],
        total: searchResponse?.results?.length || 0,
        hasMore: searchResponse?.has_more || false,

        // Loading state
        isLoading,
        error,

        // Actions
        setQuery,
        handleSearch,
        handleRecentSearchClick,
        clearQuery,
        updateFilters,
        clearFilters,
        updateScopes,
        refetch,
        refetchRecentSearches,
        refetchPopularSearches,
        saveRecentSearch,
    }
}
